const DATABASE_NAME = 'nynch-human-coaching-recordings';
const STORE_NAME = 'drafts';
let signedInAccount: string | null = null;
export function setCoachingDraftAccount(account: string | null) { signedInAccount = account; }
export function requireDraftAccount(key: string) {
  if (!signedInAccount || !key.startsWith(`nynch-coaching:${signedInAccount}:`)) throw new Error('Draft account is no longer signed in.');
}

export interface RecordingDraft {
  id: string;
  draftKey: string;
  recordingUUID: string;
  kind: 'audio' | 'video' | 'screen';
  mimeType: string;
  chunks: Blob[];
  durationSeconds: number;
  startedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('This browser cannot save recording recovery data.'));
    const request = indexedDB.open(DATABASE_NAME, 3);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      if (!request.result.objectStoreNames.contains('chunks')) request.result.createObjectStore('chunks', { keyPath: ['recordingId','sequence'] }).createIndex('recordingId','recordingId');
      if (!request.result.objectStoreNames.contains('outbox')) request.result.createObjectStore('outbox');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open recording recovery storage.'));
  });
}

export async function saveRecordingDraft(draft: RecordingDraft): Promise<void> {
  const db = await openDb();
  try {
    requireDraftAccount(draft.draftKey);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(draft);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Could not save recording recovery data.'));
    });
  } finally { db.close(); }
}

export async function loadRecordingDrafts(draftKey: string): Promise<RecordingDraft[]> {
  if (!signedInAccount || !draftKey.startsWith(`nynch-coaching:${signedInAccount}:`)) return [];
  const db = await openDb();
  try {
    const drafts = await new Promise<RecordingDraft[]>((resolve, reject) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve((request.result as RecordingDraft[]).filter(item => item.draftKey === draftKey));
      request.onerror = () => reject(request.error ?? new Error('Could not read recording recovery data.'));
    });
    for (const draft of drafts) {
      const parts = await new Promise<Array<{ blob: Blob }>>((resolve,reject) => {
        const request = db.transaction('chunks','readonly').objectStore('chunks').index('recordingId').getAll(draft.id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      if (parts.length) draft.chunks = parts.map(part => part.blob);
    }
    return signedInAccount && draftKey.startsWith(`nynch-coaching:${signedInAccount}:`) ? drafts : [];
  } finally { db.close(); }
}

export async function clearRecordingDraft(draftKey: string, recordingId?: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_NAME,'chunks'], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => (request.result as RecordingDraft[]).filter(item => item.draftKey === draftKey && (!recordingId || item.id === recordingId)).forEach(item => {
        store.delete(item.id);
        const cursor = tx.objectStore('chunks').index('recordingId').openCursor(IDBKeyRange.only(item.id));
        cursor.onsuccess = () => { if (cursor.result) { cursor.result.delete(); cursor.result.continue(); } };
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Could not clear recording recovery data.'));
    });
  } finally { db.close(); }
}

export async function saveRecordingChunk(draft: Omit<RecordingDraft,'chunks'>,sequence: number,blob: Blob): Promise<void> {
  const db = await openDb();
  try {
    requireDraftAccount(draft.draftKey);
    await new Promise<void>((resolve,reject) => {
      const tx = db.transaction([STORE_NAME,'chunks'],'readwrite');
      tx.objectStore(STORE_NAME).put({ ...draft,chunks:[] });
      tx.objectStore('chunks').put({ recordingId:draft.id,sequence,blob });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Recording save interrupted'));
    });
  } finally { db.close(); }
}

export async function clearAllCoachingDrafts(): Promise<void> {
  const db = await openDb();
  try { await new Promise<void>((resolve,reject) => {
    const tx = db.transaction([STORE_NAME,'chunks','outbox'],'readwrite');
    tx.objectStore(STORE_NAME).clear(); tx.objectStore('chunks').clear();
    tx.objectStore('outbox').clear();
    tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error);
  }); } finally { db.close(); }
}

export async function readCoachingOutbox<T>(key: string): Promise<T | undefined> {
  if (!signedInAccount || !key.startsWith(`nynch-coaching:${signedInAccount}:`)) return undefined;
  const db = await openDb();
  try { return await new Promise<T | undefined>((resolve,reject) => {
    const request = db.transaction('outbox').objectStore('outbox').get(key);
    request.onsuccess = () => resolve(signedInAccount && key.startsWith(`nynch-coaching:${signedInAccount}:`) ? request.result : undefined); request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
export async function writeCoachingOutbox(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  try { requireDraftAccount(key); await new Promise<void>((resolve,reject) => {
    const tx = db.transaction('outbox','readwrite');
    if (value === undefined) tx.objectStore('outbox').delete(key); else tx.objectStore('outbox').put(value,key);
    tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('Draft save interrupted'));
  }); } finally { db.close(); }
}
